import { addDays, subMinutes } from 'date-fns';

import { OrderEntity, ProjectEntity } from '@/common/types/entities';
import { WorkerThresholdExceededAlert } from '@/features/capacities/types';
import { formatDateKey } from '@/features/capacities/utils';
import { ProjectContributorGroupFragment } from '@/graphql/__generated__/graphql';
import { getRandomId } from '@/helpers/utils/strings';

type DayKey = string;
type ContributorGroupKey = string;
type SimultaneousWorkersOfContributorGroupsByDay = Map<
  ContributorGroupKey,
  Map<DayKey, { workers: number; orderIds: string[] }>
>;
type SimultaneousWorkersOfProjectByDay = Map<DayKey, { workers: number; orderIds: string[] }>;

export const calculateWorkerThresholdExceededAlerts = (
  orders: Map<string, OrderEntity>,
  contributorGroups: Map<string, ProjectContributorGroupFragment>,
  project: ProjectEntity,
  isStartDateDuringWorkingTime: (date: Date, calendarId: string) => boolean,
): WorkerThresholdExceededAlert[] => {
  const { simultaneousWorkersOfContributorGroupsByDay, simultaneousWorkersOfProjectByDay } =
    aggregateSimultaneousWorkersByDay(orders, contributorGroups, isStartDateDuringWorkingTime);

  return calculateWorkerThresholdExceededAlertsFromAggregatedWorkersByDay(
    contributorGroups,
    project,
    simultaneousWorkersOfContributorGroupsByDay,
    simultaneousWorkersOfProjectByDay,
  );
};

const aggregateSimultaneousWorkersByDay = (
  orders: Map<string, OrderEntity>,
  contributorGroups: Map<string, ProjectContributorGroupFragment>,
  isStartDateDuringWorkingTime: (date: Date, calendarId: string) => boolean,
) => {
  const simultaneousWorkersOfContributorGroupsByDay: SimultaneousWorkersOfContributorGroupsByDay =
    new Map();
  const simultaneousWorkersOfProjectByDay: SimultaneousWorkersOfProjectByDay = new Map();

  for (const order of orders.values()) {
    const contributorGroup =
      order.contributorGroup && contributorGroups.get(order.contributorGroup.id);

    const contributorGroupId = contributorGroup?.id;
    const start = order.startAt;
    const end = subMinutes(order.finishAt, 1);

    let currentDate = start;

    while (currentDate.getTime() <= end.getTime()) {
      const dayKey = formatDateKey(currentDate);

      const isWorkingTime = isStartDateDuringWorkingTime(currentDate, order.calendar.id);

      currentDate = addDays(currentDate, 1);

      if (!isWorkingTime) continue;

      const projectWorkersOnCurrentDay = simultaneousWorkersOfProjectByDay.get(dayKey);

      if (projectWorkersOnCurrentDay) {
        projectWorkersOnCurrentDay.workers += order.estimatedWorkers || 0;
        projectWorkersOnCurrentDay.orderIds.push(order.id);
      } else {
        simultaneousWorkersOfProjectByDay.set(dayKey, {
          workers: order.estimatedWorkers || 0,
          orderIds: [order.id],
        });
      }

      if (!contributorGroupId) continue;

      const contributorGroupWorkersByDay =
        simultaneousWorkersOfContributorGroupsByDay.get(contributorGroupId);

      if (!contributorGroupWorkersByDay) {
        simultaneousWorkersOfContributorGroupsByDay.set(
          contributorGroupId,
          new Map([[dayKey, { workers: order.estimatedWorkers || 0, orderIds: [order.id] }]]),
        );
      } else {
        const contributorGroupWorkersOnCurrentDay = contributorGroupWorkersByDay.get(dayKey);
        if (contributorGroupWorkersOnCurrentDay) {
          contributorGroupWorkersOnCurrentDay.workers += order.estimatedWorkers || 0;
          contributorGroupWorkersOnCurrentDay.orderIds.push(order.id);
        } else {
          contributorGroupWorkersByDay.set(dayKey, {
            workers: order.estimatedWorkers || 0,
            orderIds: [order.id],
          });
        }
      }
    }
  }

  return {
    simultaneousWorkersOfContributorGroupsByDay,
    simultaneousWorkersOfProjectByDay,
  };
};

const calculateWorkerThresholdExceededAlertsFromAggregatedWorkersByDay = (
  contributorGroups: Map<string, ProjectContributorGroupFragment>,
  project: ProjectEntity,
  simultaneousWorkersOfContributorGroupsByDay: SimultaneousWorkersOfContributorGroupsByDay,
  simultaneousWorkersOfProjectByDay: SimultaneousWorkersOfProjectByDay,
): WorkerThresholdExceededAlert[] => {
  const workerThresholdExceededAlerts: WorkerThresholdExceededAlert[] = [];

  addContributorGroupWorkerThresholdExceededAlerts(
    workerThresholdExceededAlerts,
    contributorGroups,
    simultaneousWorkersOfContributorGroupsByDay,
  );

  addProjectWorkerThresholdExceededAlerts(
    workerThresholdExceededAlerts,
    project,
    simultaneousWorkersOfProjectByDay,
  );

  return workerThresholdExceededAlerts;
};

const addContributorGroupWorkerThresholdExceededAlerts = (
  workerThresholdExceededAlerts: WorkerThresholdExceededAlert[],
  contributorGroups: Map<string, ProjectContributorGroupFragment>,
  simultaneousWorkersOfContributorGroupsByDay: SimultaneousWorkersOfContributorGroupsByDay,
) => {
  simultaneousWorkersOfContributorGroupsByDay.forEach(
    (maxSimultaneousWorkersOfContributorGroupPerDay, contributorGroupId) => {
      const contributorGroupWorkerThreshold =
        contributorGroups.get(contributorGroupId)?.workerThreshold;
      if (contributorGroupWorkerThreshold === null || contributorGroupWorkerThreshold === undefined)
        return;

      for (const [
        dayKey,
        maxSimultaneousWorkers,
      ] of maxSimultaneousWorkersOfContributorGroupPerDay.entries()) {
        if (maxSimultaneousWorkers.workers > contributorGroupWorkerThreshold) {
          const contributorGroup = contributorGroups.get(contributorGroupId);
          if (!contributorGroup) continue;

          workerThresholdExceededAlerts.push({
            id: getRandomId(),
            date: new SchedulingDate(dayKey),
            orderIds: maxSimultaneousWorkers.orderIds,
            type: 'contributorGroup',
            contributorGroupId: contributorGroupId,
          });
        }
      }
    },
  );
};

const addProjectWorkerThresholdExceededAlerts = (
  workerThresholdExceededAlerts: WorkerThresholdExceededAlert[],
  project: ProjectEntity,
  simultaneousWorkersOfProjectByDay: SimultaneousWorkersOfProjectByDay,
) => {
  if (project.workerThreshold === null || project.workerThreshold === undefined)
    return workerThresholdExceededAlerts;

  simultaneousWorkersOfProjectByDay.forEach((maxSimultaneousWorkers, dayKey) => {
    const projectWorkerThreshold = project.workerThreshold ?? 0; // is defined by now so this should never but typescript can't evaluate bc of scope
    if (maxSimultaneousWorkers.workers > projectWorkerThreshold) {
      workerThresholdExceededAlerts.push({
        id: getRandomId(),
        date: new SchedulingDate(dayKey),
        orderIds: maxSimultaneousWorkers.orderIds,
        type: 'project',
      });
    }
  });
};
